import React, { useEffect, useRef, useState } from "react";
import { Check, CheckCheck, Eye, Sparkles, Clock, Play, Pause } from "lucide-react";
import { getVibe } from "./VibePicker";
import ReactionPicker, { ReactionChips } from "./ReactionPicker";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
function mediaSrc(m) {
  if (!m) return null;
  if (m.startsWith("data:")) return m;
  if (m.startsWith("http")) return m;
  return `${BACKEND_URL}${m}`;
}

function formatTime(iso) {
  try {
    return new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  } catch { return ""; }
}

function VibeBubbleClasses(vibeKey, isMe) {
  const v = getVibe(vibeKey);
  if (!v) return { style: {}, anim: "" };
  const style = { background: v.bg, borderColor: "#0A0A0A", color: "#0A0A0A", boxShadow: `4px 4px 0 0 ${v.color}` };
  const animMap = {
    fire: "vibe-anim-fire",
    ice: "vibe-anim-ice",
    playful: "vibe-anim-playful",
    sad: "vibe-anim-sad",
    urgent: "vibe-anim-urgent",
    romantic: "vibe-anim-romantic",
  };
  return { style, anim: animMap[vibeKey] || "" };
}

function VoicePlayer({ src, duration }) {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const toggle = () => {
    const a = audioRef.current;
    if (!a) return;
    if (playing) { a.pause(); } else { a.play(); }
  };
  useEffect(() => {
    const a = audioRef.current;
    if (!a) return;
    const p = () => setPlaying(true);
    const s = () => setPlaying(false);
    a.addEventListener("play", p);
    a.addEventListener("pause", s);
    a.addEventListener("ended", s);
    return () => {
      a.removeEventListener("play", p);
      a.removeEventListener("pause", s);
      a.removeEventListener("ended", s);
    };
  }, []);

  const bars = Array.from({ length: 22 }).map((_, i) => 6 + Math.round(18 * Math.abs(Math.sin(i * 1.7 + duration))));
  return (
    <div className="flex items-center gap-3 min-w-[210px]" data-testid="voice-player">
      <button onClick={toggle} className="w-9 h-9 rounded-full bg-[#0A0A0A] text-white flex items-center justify-center press-btn shadow-hard-sm" aria-label="Play">
        {playing ? <Pause strokeWidth={3} className="w-4 h-4" /> : <Play strokeWidth={3} className="w-4 h-4" />}
      </button>
      <div className="flex items-end gap-[3px] flex-1">
        {bars.map((h, i) => (
          <span key={i} className={`bg-[#0A0A0A] w-[3px] rounded-sm ${playing ? "opacity-80" : "opacity-60"}`} style={{ height: h }} />
        ))}
      </div>
      <span className="text-xs font-black tabular-nums">{(duration || 0).toFixed(1)}s</span>
      <audio ref={audioRef} src={src} preload="metadata" />
    </div>
  );
}

export default function MessageBubble({ message, isMe, totalOthers, isGroup, senderName, hideMeta = false, myId, onReactionChange }) {
  const isAI = !!message.is_ai;
  const isWhisper = !!message.whisper;
  const [revealed, setRevealed] = useState(false);
  const revealTimer = useRef(null);

  useEffect(() => () => clearTimeout(revealTimer.current), []);

  const readByOthers = (message.read_by || []).filter((uid) => uid !== message.sender_id).length;
  const allRead = totalOthers > 0 && readByOthers >= totalOthers;

  const { style: vibeStyle, anim: vibeAnim } = VibeBubbleClasses(message.vibe, isMe);

  const bubbleBase = "px-4 py-2.5 border-[2px] border-[#0A0A0A] rounded-2xl font-medium text-[15px] leading-snug shadow-hard-sm";
  const bubbleColor = isAI
    ? "bg-gradient-to-br from-[#D4B3FF] to-[#F0DFFF]"
    : isMe
    ? "bg-[#D4B3FF] rounded-tr-sm"
    : "bg-white rounded-tl-sm";

  const bubbleCls = `${bubbleBase} ${bubbleColor} ${vibeAnim}`;

  const media = message.media_url || message.media_data;
  const isDoodle = message.content_type === "doodle" && media;
  const isVoice = message.content_type === "voice" && media;
  const isImage = message.content_type === "image" && media;
  const isVideo = message.content_type === "video" && media;
  const src = mediaSrc(media);

  const showBody = !isWhisper || revealed;

  const reveal = () => {
    if (!isWhisper || revealed) return;
    setRevealed(true);
    revealTimer.current = setTimeout(() => setRevealed(false), 5000);
  };

  return (
    <div className={`flex mb-3 bubble-pop-in ${isMe ? "justify-end" : "justify-start"}`} data-testid="message-row">
      <div className={`flex flex-col max-w-[80%] ${isMe ? "items-end" : "items-start"}`}>
        {(isGroup || isAI) && !isMe && (
          <span className={`text-xs font-black uppercase tracking-wider mb-1 ml-1 ${isAI ? "text-[#7C3AED]" : "text-[#555]"}`}>
            {isAI && <Sparkles strokeWidth={3} className="inline w-3 h-3 mr-1" />}
            {senderName}
          </span>
        )}
        <div
          data-testid={isMe ? "message-bubble-sent" : "message-bubble-received"}
          data-vibe={message.vibe || ""}
          onClick={reveal}
          className={bubbleCls}
          style={{ wordBreak: "break-word", whiteSpace: "pre-wrap", ...vibeStyle, cursor: isWhisper && !revealed ? "pointer" : "default" }}
        >
          {isWhisper && !revealed && (
            <div className="flex items-center gap-2 text-[#555] font-bold" data-testid="whisper-locked">
              <Eye strokeWidth={2.5} className="w-4 h-4" /> Fısıltı — tıkla, 5 sn görünsün
            </div>
          )}
          {showBody && (
            <>
              {message.reel_ref && (
                <a
                  href="/app/reels"
                  data-testid="message-reel-ref"
                  className="block bg-black text-white border-hard-2 rounded-xl overflow-hidden mb-2 max-w-[260px]"
                >
                  <div className="flex items-center gap-2 p-2">
                    <div className="w-12 h-16 bg-[#222] rounded-md overflow-hidden shrink-0">
                      {message.reel_ref.media_type === "video" ? (
                        <video src={mediaSrc(message.reel_ref.media_url)} className="w-full h-full object-cover" muted playsInline />
                      ) : (
                        <img src={mediaSrc(message.reel_ref.media_url)} alt="" className="w-full h-full object-cover" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-[10px] uppercase font-black tracking-widest text-[#CCFF00]">🎬 SNAP</div>
                      <div className="text-xs font-bold truncate">{message.reel_ref.caption || "Snap"}</div>
                      <div className="text-[10px] text-white/60">Görüntülemek için dokun</div>
                    </div>
                  </div>
                </a>
              )}
              {isDoodle && (
                <img src={src} alt="doodle" className="rounded-lg max-w-[300px] border-hard-2" data-testid="doodle-image" />
              )}
              {isImage && (
                <img
                  src={src}
                  alt=""
                  className="rounded-xl max-w-[320px] max-h-[400px] border-hard-2 shadow-hard-sm object-cover cursor-zoom-in"
                  data-testid="chat-image"
                  loading="lazy"
                  onClick={() => window.open(src, "_blank", "noopener")}
                />
              )}
              {isVideo && (
                <video
                  src={src}
                  className="rounded-xl max-w-[320px] max-h-[420px] border-hard-2 shadow-hard-sm bg-black"
                  data-testid="chat-video"
                  controls
                  playsInline
                  preload="metadata"
                />
              )}
              {isVoice && (
                <VoicePlayer src={src} duration={message.media_duration || 0} />
              )}
              {(!isDoodle && !isVoice && !isImage && !isVideo) && (message.content || "")}
            </>
          )}
        </div>

        {/* Reactions row */}
        <div className={`flex items-center gap-2 mt-1 ${isMe ? "flex-row-reverse" : "flex-row"}`}>
          <ReactionChips
            reactions={message.reactions}
            myId={myId}
            onToggle={async (emoji) => {
              try {
                const { default: api } = await import("@/lib/api");
                const { data } = await api.post(`/messages/${message.id}/react`, { emoji });
                onReactionChange?.(message.id, data.reactions || {});
              } catch {}
            }}
          />
          {!message.whisper && !hideMeta && myId && (
            <ReactionPicker
              messageId={message.id}
              isMe={isMe}
              currentReactions={message.reactions || {}}
              myId={myId}
              onChanged={(r) => onReactionChange?.(message.id, r)}
            />
          )}
        </div>

        {!hideMeta && (
          <div className="flex items-center gap-1 mt-1 mr-1 text-[11px] font-bold text-[#555]">
            {message.expires_at && (
              <span className="flex items-center gap-1" data-testid="ephemeral-indicator">
                <Clock strokeWidth={3} className="w-3 h-3" />
              </span>
            )}
            <span data-testid="message-time">{formatTime(message.created_at)}</span>
            {isMe && !isAI && (
              <span
                data-testid={allRead ? "read-receipt-read" : "read-receipt-sent"}
                title={allRead ? "Okundu" : "Gönderildi"}
                className={allRead ? "text-[#0A0A0A]" : "text-[#999]"}
              >
                {allRead ? <CheckCheck strokeWidth={3} className="w-4 h-4" /> : <Check strokeWidth={3} className="w-4 h-4" />}
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
